import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  PersonOutline,
  LockOutlined,
} from "@mui/icons-material";
import { useAuth } from "../../AuthContext";
import { API_BASE, demo } from "./demoStyles";

// ── Slide data ──────────────────────────────────────────────────────────────
const SLIDES = [
  {
    img: "/assets/Designer3.jpg",
    tag: "Solar Water Pumping",
    title: "Powering farms with\npure solar energy",
    subtitle: "Reliable, emission-free water delivery for agriculture across India.",
    stat: "50K+",
    statLabel: "Pumps deployed",
  },
  {
    img: "/assets/chargerImage.png",
    tag: "EV Charging",
    title: "Seamless charging for\nan Electric India",
    subtitle: "From 14 kW AC to 120 kW DC fast chargers — powering India's EV revolution.",
    stat: "248+",
    statLabel: "Chargers deployed",
  },
  {
    img: "/assets/solarInverter.png",
    tag: "Solar Inverters",
    title: "New age solar inverters\nwith advanced technology",
    subtitle: "High-efficiency solar inverters with real-time monitoring and smart grid integration.",
    stat: "42 MW",
    statLabel: "Solar capacity managed",
  },
  {
    img: "/assets/servoStabilizer.png",
    tag: "Servo Stabilizers",
    title: "Take control of\nvoltage fluctuations",
    subtitle: "Protect every device on your network with intelligent voltage regulation.",
    stat: "99.9%",
    statLabel: "Power stability",
  },
];

// ── Floating particle ────────────────────────────────────────────────────────
const Particle = ({ left, delay, size, duration }) => (
  <Box
    sx={{
      position: "absolute",
      bottom: "-10px",
      left,
      width: size,
      height: size,
      borderRadius: "50%",
      background: `radial-gradient(circle, ${demo.accent}, transparent)`,
      opacity: 0,
      animation: `floatUp ${duration} ${delay} ease-in-out infinite`,
      "@keyframes floatUp": {
        "0%":   { transform: "translateY(0)   scale(1)",   opacity: 0 },
        "15%":  { opacity: 0.7 },
        "85%":  { opacity: 0.3 },
        "100%": { transform: "translateY(-140px) scale(0.4)", opacity: 0 },
      },
      pointerEvents: "none",
    }}
  />
);

// ── Main component ───────────────────────────────────────────────────────────
const DemoLogin = () => {
  const navigate   = useNavigate();
  const { setTokens } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState("");
  const [form, setForm]                 = useState({ username: "", password: "" });
  const [slide, setSlide]               = useState(0);
  const [visible, setVisible]           = useState(false); // mount animation

  // Trigger entry animation after first paint
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 5500);
    return () => clearInterval(t);
  }, []);

  const onChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
    if (error) setError("");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.password.trim()) {
      setError("Please enter your username and password.");
      return;
    }
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: form.username, password: form.password }),
      });
      const data = await res.json();
      if (res.ok) {
        setTokens(data.access_token, data.refresh_token, true);
        navigate("/demo");
      } else {
        setError(data.message || "Invalid username or password.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Shared field style ─────────────────────────────────────────────────────
  const fieldSx = {
    mb: 2.2,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.03)",
      borderRadius: "12px",
      color: demo.text,
      fontSize: 14.5,
      "& fieldset":        { borderColor: "rgba(255,255,255,0.10)", transition: "border-color 0.25s" },
      "&:hover fieldset":  { borderColor: "rgba(255,255,255,0.22)" },
      "&.Mui-focused fieldset": { borderColor: demo.primary, boxShadow: `0 0 0 3px rgba(59,130,246,0.18)` },
    },
    "& .MuiInputBase-input": { py: "13px" },
    "& textarea::placeholder, & input::placeholder": { color: demo.textFaint, opacity: 1 },
    "& .MuiSvgIcon-root": { color: demo.textDim },
  };

  // ── Particles config ───────────────────────────────────────────────────────
  const particles = [
    { left: "6%",  delay: "0s",    size: "5px", duration: "5s"  },
    { left: "14%", delay: "0.8s",  size: "3px", duration: "6.5s"},
    { left: "23%", delay: "1.6s",  size: "4px", duration: "4.8s"},
    { left: "35%", delay: "0.3s",  size: "3px", duration: "7s"  },
    { left: "48%", delay: "2s",    size: "5px", duration: "5.5s"},
    { left: "60%", delay: "1.1s",  size: "3px", duration: "6s"  },
    { left: "72%", delay: "0.6s",  size: "4px", duration: "4.5s"},
    { left: "83%", delay: "1.8s",  size: "3px", duration: "7.5s"},
    { left: "91%", delay: "0.4s",  size: "5px", duration: "5.2s"},
  ];

  return (
    <Box sx={{ minHeight: "100vh", width: "100%", display: "flex", background: demo.bg, overflow: "hidden" }}>

      {/* ════════════════════════════════════════════════════════════
          LEFT PANEL — animated image showcase
      ════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          flex: "0 0 58%",
          position: "relative",
          overflow: "hidden",
          display: { xs: "none", lg: "flex" },
          flexDirection: "column",
        }}
      >
        {/* Image slides */}
        {SLIDES.map((s, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${s.img})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: slide === i ? 1 : 0,
              transition: "opacity 1.4s cubic-bezier(0.4, 0, 0.2, 1)",
              // subtle Ken Burns on active slide
              transform: slide === i ? "scale(1.04)" : "scale(1)",
              transformOrigin: "center center",
              transitionDuration: slide === i ? "opacity 1.4s, transform 8s" : "opacity 1.4s",
            }}
          />
        ))}

        {/* Multi-layer overlay */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: [
              "linear-gradient(to right, transparent 50%, rgba(7,11,20,0.65) 100%)",
              "linear-gradient(to top, rgba(7,11,20,0.88) 0%, rgba(7,11,20,0.18) 55%, transparent 100%)",
              "linear-gradient(to bottom, rgba(7,11,20,0.45) 0%, transparent 20%)",
            ].join(","),
            zIndex: 1,
          }}
        />

        {/* Particles */}
        <Box sx={{ position: "absolute", inset: 0, zIndex: 2, pointerEvents: "none" }}>
          {particles.map((p, i) => <Particle key={i} {...p} />)}
        </Box>

        {/* Top-left brand pill */}
        <Box
          sx={{
            position: "absolute",
            top: 30,
            left: 32,
            zIndex: 4,
            display: "flex",
            alignItems: "center",
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.6,
              py: 0.7,
              borderRadius: "50px",
              background: "rgba(0,0,0,0.35)",
              border: "1px solid rgba(255,255,255,0.15)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: demo.green,
                boxShadow: `0 0 8px ${demo.green}`,
                animation: "pulse 2.2s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1, transform: "scale(1)" },
                  "50%":      { opacity: 0.45, transform: "scale(0.8)" },
                },
              }}
            />
            <Typography sx={{ color: "rgba(255,255,255,0.80)", fontSize: 12, fontWeight: 700, letterSpacing: "0.07em" }}>
              SERVOTECH ONE
            </Typography>
          </Box>
        </Box>

        {/* Slide label pill (top right of image area) */}
        <Box
          sx={{
            position: "absolute",
            top: 30,
            right: 30,
            zIndex: 4,
          }}
          key={`tag-${slide}`}
        >
          <Box
            sx={{
              px: 1.6,
              py: 0.6,
              borderRadius: "8px",
              background: `linear-gradient(135deg, rgba(59,130,246,0.28), rgba(34,211,238,0.18))`,
              border: `1px solid rgba(34,211,238,0.35)`,
              backdropFilter: "blur(8px)",
              animation: "fadeSlideDown 0.5s ease forwards",
              "@keyframes fadeSlideDown": {
                from: { opacity: 0, transform: "translateY(-8px)" },
                to:   { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            <Typography sx={{ color: demo.accent, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em" }}>
              {SLIDES[slide].tag.toUpperCase()}
            </Typography>
          </Box>
        </Box>

        {/* Bottom content */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 3,
            px: 4,
            py: 4.5,
          }}
        >
          {/* Stat badge */}
          <Box
            key={`stat-${slide}`}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.2,
              px: 2,
              py: 0.8,
              borderRadius: "50px",
              background: "rgba(0,0,0,0.35)",
              border: `1px solid rgba(34,211,238,0.3)`,
              backdropFilter: "blur(12px)",
              mb: 2.2,
              animation: "fadeUp 0.55s ease forwards",
              "@keyframes fadeUp": {
                from: { opacity: 0, transform: "translateY(16px)" },
                to:   { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            <Typography sx={{ color: demo.accent, fontWeight: 800, fontSize: 15, lineHeight: 1 }}>
              {SLIDES[slide].stat}
            </Typography>
            <Box sx={{ width: 1, height: 14, background: "rgba(255,255,255,0.2)", borderRadius: 1 }} />
            <Typography sx={{ color: "rgba(255,255,255,0.60)", fontSize: 12, fontWeight: 500 }}>
              {SLIDES[slide].statLabel}
            </Typography>
          </Box>

          {/* Title */}
          <Typography
            key={`title-${slide}`}
            sx={{
              color: "#fff",
              fontWeight: 900,
              fontSize: { lg: 33, xl: 40 },
              lineHeight: 1.15,
              mb: 1.2,
              textShadow: "0 4px 24px rgba(0,0,0,0.55)",
              whiteSpace: "pre-line",
              animation: "fadeUp 0.65s 0.08s ease both",
            }}
          >
            {SLIDES[slide].title}
          </Typography>

          {/* Subtitle */}
          <Typography
            key={`sub-${slide}`}
            sx={{
              color: "rgba(255,255,255,0.60)",
              fontSize: 14.5,
              lineHeight: 1.6,
              mb: 3.5,
              maxWidth: 480,
              animation: "fadeUp 0.65s 0.16s ease both",
            }}
          >
            {SLIDES[slide].subtitle}
          </Typography>

          {/* Dot indicators */}
          <Box sx={{ display: "flex", gap: 0.8, alignItems: "center" }}>
            {SLIDES.map((_, i) => (
              <Box
                key={i}
                onClick={() => setSlide(i)}
                sx={{
                  height: 4,
                  width: slide === i ? 34 : 14,
                  borderRadius: "2px",
                  background: slide === i
                    ? `linear-gradient(90deg, ${demo.primary}, ${demo.accent})`
                    : "rgba(255,255,255,0.28)",
                  transition: "all 0.45s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  "&:hover": { background: slide === i ? undefined : "rgba(255,255,255,0.5)" },
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* ════════════════════════════════════════════════════════════
          RIGHT PANEL — login form
      ════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 3, sm: 5, lg: 5.5 },
          py: 4,
          position: "relative",
          background: demo.bgGradient,
          // Entry animation
          opacity:   visible ? 1 : 0,
          transform: visible ? "translateX(0)" : "translateX(32px)",
          transition: "opacity 0.75s ease, transform 0.75s ease",
        }}
      >
        {/* Ambient glow blobs */}
        <Box sx={{
          position: "absolute", top: "-80px", right: "-80px",
          width: 340, height: 340, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(59,130,246,0.10) 0%, transparent 70%)",
          pointerEvents: "none",
        }} />
        <Box sx={{
          position: "absolute", bottom: "-60px", left: "-60px",
          width: 260, height: 260, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(34,211,238,0.07) 0%, transparent 70%)",
          pointerEvents: "none",
        }} />

        <Box sx={{ width: "100%", maxWidth: 400, position: "relative", zIndex: 1 }}>

          {/* ── Logo block ── */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 4 }}>
            {/* Animated logo container */}
            <Box
              sx={{
                mb: 2,
                px: 3,
                py: 1.8,
                borderRadius: "18px",
                background: "rgba(255,255,255,0.92)",
                border: "1px solid rgba(255,255,255,0.35)",
                backdropFilter: "blur(12px)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.35), 0 0 0 1px rgba(59,130,246,0.12)",
                animation: "logoFloat 5s ease-in-out infinite",
                "@keyframes logoFloat": {
                  "0%, 100%": { transform: "translateY(0px)" },
                  "50%":      { transform: "translateY(-6px)" },
                },
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  borderRadius: "18px",
                  background: `linear-gradient(135deg, rgba(59,130,246,0.12), rgba(34,211,238,0.08))`,
                  opacity: 0,
                  transition: "opacity 0.3s",
                },
                "&:hover::after": { opacity: 1 },
              }}
            >
              <img
                src="/assets/user.png"
                alt="Servotech"
                style={{
                  height: 46,
                  display: "block",
                  filter: "drop-shadow(0 2px 10px rgba(59,130,246,0.4)) brightness(1.05)",
                }}
              />
            </Box>

            {/* "Unified Control Center" pill */}
            <Box
              sx={{
                px: 1.6,
                py: 0.5,
                borderRadius: "6px",
                background: "rgba(34,211,238,0.07)",
                border: "1px solid rgba(34,211,238,0.18)",
              }}
            >
              <Typography sx={{ color: demo.accent, fontSize: 10.5, fontWeight: 800, letterSpacing: "0.13em" }}>
                UNIFIED CONTROL CENTER
              </Typography>
            </Box>
          </Box>

          {/* ── Headings ── */}
          <Typography sx={{ color: demo.text, fontWeight: 800, fontSize: 27, mb: 0.5, textAlign: "center", letterSpacing: "-0.3px" }}>
            Welcome back
          </Typography>
          <Typography sx={{ color: demo.textDim, fontSize: 14, mb: 3.5, textAlign: "center", lineHeight: 1.5 }}>
            Sign in to manage your renewable infrastructure
          </Typography>

          {/* ── Form card ── */}
          <Box
            component="form"
            onSubmit={handleLogin}
            noValidate
            sx={{
              background: "rgba(255,255,255,0.025)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "22px",
              p: "28px 28px 24px",
              backdropFilter: "blur(16px)",
              boxShadow: "0 24px 64px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
              position: "relative",
              overflow: "hidden",
              // Top shimmer line
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0, left: "-100%",
                width: "100%", height: "1px",
                background: `linear-gradient(90deg, transparent, ${demo.accent}, transparent)`,
                animation: "sweepLine 4s ease-in-out infinite",
                "@keyframes sweepLine": {
                  "0%":   { left: "-100%" },
                  "50%":  { left: "100%" },
                  "100%": { left: "100%" },
                },
              },
            }}
          >
            <TextField
              fullWidth
              name="username"
              placeholder="Username"
              autoFocus
              value={form.username}
              onChange={onChange}
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutline fontSize="small" sx={{ color: demo.textDim }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              name="password"
              placeholder="Password"
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={onChange}
              sx={{ ...fieldSx, mb: error ? 2 : 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined fontSize="small" sx={{ color: demo.textDim }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((s) => !s)}
                      edge="end"
                      size="small"
                      sx={{ color: demo.textDim, mr: 0.2 }}
                    >
                      {showPassword
                        ? <Visibility fontSize="small" />
                        : <VisibilityOff fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {error && (
              <Box
                sx={{
                  px: 1.8,
                  py: 1,
                  borderRadius: "10px",
                  background: "rgba(248,113,113,0.08)",
                  border: "1px solid rgba(248,113,113,0.22)",
                  mb: 2.2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography sx={{ color: "#f87171", fontSize: 13 }}>⚠ {error}</Typography>
              </Box>
            )}

            <Button
              type="submit"
              fullWidth
              disabled={loading}
              sx={{
                mt: error ? 0 : 2.5,
                py: 1.5,
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: 15,
                textTransform: "none",
                color: "#fff",
                background: `linear-gradient(135deg, ${demo.primary} 0%, ${demo.accent} 100%)`,
                boxShadow: `0 8px 28px rgba(34,211,238,0.22)`,
                transition: "all 0.28s ease",
                "&:hover": {
                  background: `linear-gradient(135deg, ${demo.primaryDeep} 0%, ${demo.primary} 100%)`,
                  boxShadow: `0 12px 36px rgba(59,130,246,0.40)`,
                  transform: "translateY(-2px)",
                },
                "&:active": { transform: "translateY(0px)" },
                "&.Mui-disabled": { color: "rgba(255,255,255,0.45)", background: "rgba(255,255,255,0.08)" },
              }}
            >
              {loading
                ? <CircularProgress size={20} sx={{ color: "#fff" }} />
                : "Sign In →"}
            </Button>
          </Box>

          {/* ── Stats strip ── */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-around",
              mt: 3.5,
              pt: 3,
              borderTop: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            {[
              { emoji: "💧", value: "50K+",  label: "Pumps" },
              { emoji: "☀️", value: "42 MW", label: "Solar" },
              { emoji: "🌿", value: "1200+", label: "Clients" },
            ].map((s, i) => (
              <Box
                key={i}
                sx={{
                  textAlign: "center",
                  px: 1,
                  animation: `fadeUp 0.6s ${0.1 + i * 0.12}s ease both`,
                }}
              >
                <Typography sx={{ fontSize: 20, mb: 0.3, lineHeight: 1 }}>{s.emoji}</Typography>
                <Typography sx={{ color: demo.text, fontWeight: 800, fontSize: 16, lineHeight: 1, mb: 0.3 }}>
                  {s.value}
                </Typography>
                <Typography sx={{ color: demo.textFaint, fontSize: 11, fontWeight: 500 }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* ── Footer ── */}
          <Typography sx={{ color: demo.textFaint, fontSize: 11, textAlign: "center", mt: 3.5, lineHeight: 1.5 }}>
            © {new Date().getFullYear()} Servotech Renewable Power System Limited
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default DemoLogin;
